import { ContentCopy, GridViewOutlined, WhatsApp } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../../theme";
import { useCallback, useEffect, useState } from "react";
import { showSuccessToast } from "../../../../components/common/ShowToast";
import { CalendarIcon, PhoneIcon } from "../../../../components/icons";
import { ArrowsExpandIcon } from "../../../../components/icons/ArrowsExpandIcon";
import { PersonIcon } from "../../../../components/icons/PersonIcon";
import { BookingData } from "../../types/view-booking";
import CustomTextField from "../../../../components/common/Textfield";
import { useShareTemplates } from "../../../configurations/share-messages/hooks/useShareMessageTemplates";

interface Props {
  open: boolean;
  onClose: () => void;
  bookingData?: BookingData | null;
}

export default function RequestClientReviewModal({
  open,
  onClose,
  bookingData,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [includeClientName, setIncludeClientName] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [message, setMessage] = useState("");

  const { templates, fetchTemplates } = useShareTemplates();
  const safeTemplates = Array.isArray(templates) ? templates : [];
  const clientReviewTemplates = safeTemplates.filter(
    (t) => t.template_type === "client_review",
  );

  const clientName = bookingData?.organiser?.name || "Client";
  const phone = bookingData?.organiser?.phone || "";
  const service = bookingData?.service || "Memorial Service";
  const referenceNumber =
    bookingData?.reference_number ?? bookingData?.id ?? "";
  const reviewLink = `${window.location.origin}/client-review/${referenceNumber}`;
  const categoryMap: Record<string, string> = {
    Funeral: "funeral",
    Conference: "conference",
    Church: "church",
  };

  const templateCategory = categoryMap[bookingData?.service ?? ""] ?? "";

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId("");
      setMessage("");
    }
  }, [open]);

  useEffect(() => {
    if (open && bookingData) {
      fetchTemplates(templateCategory, "client_review");
    }
  }, [open, bookingData, fetchTemplates, templateCategory]);

  const rawDate = bookingData?.streams?.[0]?.date;
  const serviceDate = rawDate
    ? (() => {
        const [day, month, year] = rawDate.split("-");
        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
        ).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      })()
    : "N/A";

  const fillTemplate = useCallback(
    (body: string, withName: boolean) => {
      let filled = body;
      const values = {
        clientName: withName ? clientName : "there",
        deceasedName: bookingData?.beloved?.name ?? "N/A",
        funeralHomeName: bookingData?.services?.[0]?.venue?.name ?? "N/A",
        companyName: "Muzukuru",
        bookingReference: String(
          bookingData?.reference_number ?? bookingData?.id ?? "N/A",
        ),
        eventName: service,
        date: serviceDate,
        time: bookingData?.streams?.[0]?.start ?? "N/A",
        venue: bookingData?.services?.[0]?.venue?.name ?? "N/A",
        address: bookingData?.services?.[0]?.venue?.address_one ?? "N/A",
        website_stream_link:
          bookingData?.streams?.[0]?.website_stream_link ?? reviewLink,
      };
      Object.entries(values).forEach(([key, val]) => {
        filled = filled.replaceAll(`{${key}}`, val);
      });
      return filled;
    },
    [bookingData, clientName, service, serviceDate, reviewLink],
  );

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = clientReviewTemplates.find((t) => t.id === templateId);
    if (template) {
      setMessage(fillTemplate(template.body_template, includeClientName));
    }
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIncludeClientName(checked);
    const template = clientReviewTemplates.find(
      (t) => t.id === selectedTemplateId,
    );
    if (template) {
      setMessage(fillTemplate(template.body_template, checked));
    }
  };

  useEffect(() => {
    if (open && clientReviewTemplates.length > 0 && !selectedTemplateId) {
      const first = clientReviewTemplates[0];
      setSelectedTemplateId(first.id);
      setMessage(fillTemplate(first.body_template, includeClientName));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientReviewTemplates.length]);

  const whatsappMessage = `${message}\n${reviewLink}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(reviewLink);
    showSuccessToast("Link copied!");
  };

  const handleShareWhatsApp = () => {
    const encoded = encodeURIComponent(whatsappMessage);
    const phoneClean = phone.replace(/\D/g, "");
    window.open(`https://wa.me/${phoneClean}?text=${encoded}`, "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 5, maxWidth: 480 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: 24, pb: 1.5 }}>
        Request client review
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {/* Client Details */}
        <Box
          sx={{
            bgcolor: colors.primary[800],
            borderRadius: 2.5,
            p: 1.5,
            mb: 2,
          }}
        >
          <Typography
            fontWeight={600}
            sx={{ mb: 1.5, display: "block", fontSize: 16 }}
          >
            Client details
          </Typography>
          <Grid container spacing={1.5}>
            {[
              {
                icon: <PersonIcon fontSize="small" />,
                label: "Name",
                value: clientName,
              },
              {
                icon: <PhoneIcon fontSize="small" />,
                label: "Phone",
                value: phone || "N/A",
              },
              {
                icon: <GridViewOutlined fontSize="small" />,
                label: "Service",
                value: service,
              },
              {
                icon: <CalendarIcon fontSize="small" />,
                label: "Date",
                value: serviceDate,
              },
            ].map(({ icon, label, value }) => (
              <Grid size={{ xs: 6 }} key={label}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 2,
                      bgcolor: colors.primary[900],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {value}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>

        {clientReviewTemplates.length > 0 ? (
          <CustomTextField
            label="Message Template"
            fullWidth
            select
            value={selectedTemplateId}
            onChange={(e) => handleTemplateChange(e.target.value)}
            sx={{ mb: 2 }}
          >
            {clientReviewTemplates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </CustomTextField>
        ) : (
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              mb: 2,
              p: 1.5,
              bgcolor: colors.primary[800],
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            No {templateCategory} client review templates found. Create one in
            configurations.
          </Typography>
        )}

        <TextField
          fullWidth
          multiline
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              fontSize: 13,
              lineHeight: 1.7,
            },
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Switch
            checked={includeClientName}
            onChange={handleToggle}
            size="medium"
            sx={{
              "& .MuiSwitch-thumb": { bgcolor: "background.paper" },
              "& .Mui-checked + .MuiSwitch-track": {
                bgcolor: "#724E41 !important",
              },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Include client name
          </Typography>
        </Stack>

        <Box
          sx={{
            bgcolor: colors.primary[800],
            borderRadius: 2.5,
            p: 1.5,
            mb: 2,
          }}
        >
          <Typography
            fontWeight={600}
            sx={{ mb: 1, display: "block", fontSize: 16 }}
          >
            Review link
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                borderRadius: 4,
                px: 1.5,
                py: 1,
                fontSize: 13,
                color: "text.secondary",
                overflow: "hidden",
                bgcolor: colors.primary[900],
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {reviewLink}
            </Box>
            <Tooltip title="Copy link">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: colors.primary[900],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={handleCopy}
              >
                <ContentCopy sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
            <Tooltip title="Open link">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: colors.primary[900],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => window.open(reviewLink, "_blank")}
              >
                <ArrowsExpandIcon sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
          </Stack>
        </Box>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleShareWhatsApp}
            startIcon={<WhatsApp />}
            sx={{
              bgcolor: "#724E41",
              textTransform: "none",
              borderRadius: 2,
              py: 1,
              fontSize: 14,
              fontWeight: 600,
              "&:hover": { bgcolor: "#5d3e33" },
            }}
          >
            Share via WhatsApp
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
